import React from 'react';
import { MessageSquare, ThumbsUp, Reply, ShieldAlert, Trash2, ChevronRight, Share2, MoreHorizontal, Flame } from 'lucide-react';
import { CommentWithUser, Language } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { calculateTimeAgo } from '../../services/formatService';
import { getTeamName } from '../../services/teamService';
import { translateText, detectLanguage } from '../../services/geminiService';
import { Loader2, Globe } from 'lucide-react';
import ImageZoomModal from '../Common/ImageZoomModal';

interface CommunityCommentCardProps {
  comment: CommentWithUser & { is_liked?: boolean, match_info?: any };
  onLike: (id: string | number) => void;
  onReply: (comment: CommentWithUser) => void;
  onReport: (id: string | number) => void;
  onDelete: (id: string | number) => void;
  currentUserId?: string;
}

const getMatchStatusLabel = (status: string, lang: string) => {
  switch (status) {
    case 'Live':
      return lang === 'ko' ? '진행중' : lang === 'ja' ? '進行中' : 'Live';
    case 'Finished':
      return lang === 'ko' ? '종료' : lang === 'ja' ? '終了' : 'Finished';
    case 'Scheduled':
      return lang === 'ko' ? '예정' : lang === 'ja' ? '予定' : 'Scheduled';
    default:
      return status;
  }
};

export const CommunityCommentCard: React.FC<CommunityCommentCardProps> = ({
  comment,
  onLike,
  onReply,
  onReport,
  onDelete,
  currentUserId
}) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [showMenu, setShowMenu] = React.useState(false);
  const [isTranslating, setIsTranslating] = React.useState(false);
  const [translatedText, setTranslatedText] = React.useState<string | null>(null);
  const [showOriginal, setShowOriginal] = React.useState(false);
  
  const detectedLang = React.useMemo(() => detectLanguage(comment.content), [comment.content]);
  const isSameLanguage = detectedLang === lang || detectedLang === 'emoji';
  const [isZoomOpen, setIsZoomOpen] = React.useState(false);

  const isAuthor = currentUserId === comment.user_id;
  const isAdmin = user?.role === 'admin';

  const handleMatchClick = () => {
    if (comment.post_id && !isNaN(Number(comment.post_id))) {
      window.navigateTo(`/game/${comment.post_id}`);
    }
  };

  const isBest = comment.likes_count >= 3;

  const handleTranslate = async () => {
    if (translatedText) {
      setShowOriginal(!showOriginal);
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateText(comment.content, lang as Language);
      if (result) {
        setTranslatedText(result);
        setShowOriginal(false);
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div 
      id={`comment-${comment.id}`}
      className={`border-b border-gray-100 p-4 md:px-6 md:py-5 transition-all relative ${isBest ? 'bg-rose-50/30' : 'bg-white hover:bg-gray-50/50'}`}
    >
      {isBest && (
        <div className="absolute top-0 right-0 px-3 py-1 bg-rose-500 rounded-bl-xl flex items-center gap-1 shadow-sm">
          <Flame size={12} className="text-white fill-current" />
          <span className="text-[10px] font-black text-white uppercase tracking-wider">Best Intelligence</span>
        </div>
      )}
      <div className="flex gap-3">
        {/* Left Side: Avatar & Thread Line */}
        <div className="flex flex-col items-center shrink-0">
          <div className="relative">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 border border-gray-100 ring-2 ring-white">
              <img 
                src={comment.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || 'U')}&background=random`} 
                alt={comment.user?.name} 
                className="w-full h-full object-cover" 
              />
            </div>
            {comment.user?.favorite_team && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full p-0.5 shadow-md border border-white z-10">
                <img src={comment.user.favorite_team.logo} alt="" className="w-full h-full object-contain" />
              </div>
            )}
          </div>
          {/* Thread Connector Line (Visual only for now) */}
          <div className="w-0.5 grow bg-gray-100 my-2 rounded-full" />
        </div>

        {/* Right Side: Content */}
        <div className="flex-1 min-w-0">
          {/* Header: User & Time */}
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[14px] font-black text-slate-900 truncate">
              {comment.user?.name}
            </span>
            <span className="text-slate-400 text-xs font-medium ml-1">
              {calculateTimeAgo(comment.created_at)}
            </span>
            
            <div className="ml-auto flex items-center gap-2 relative">
               <button 
                 onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                 className="text-slate-400 hover:text-slate-900 transition-colors p-1"
               >
                 <MoreHorizontal size={16} />
               </button>
               {showMenu && (
                 <>
                   <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                   <div className="absolute top-full right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl py-1 z-20 min-w-[100px] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                      <button 
                        onClick={() => { onReport(comment.id); setShowMenu(false); }}
                        className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <ShieldAlert size={14} /> {t(lang, 'report')}
                      </button>
                      {(isAuthor || isAdmin) && (
                        <button 
                          onClick={() => { onDelete(comment.id); setShowMenu(false); }}
                          className="w-full text-left px-3 py-2 text-xs font-bold text-rose-500 hover:bg-rose-50 flex items-center gap-2"
                        >
                          <Trash2 size={14} /> {t(lang, 'delete')}
                        </button>
                      )}
                   </div>
                 </>
               )}
            </div>
          </div>

          {/* Match Context Badge (SNS Style) */}
          {comment.match_info && (
            <div 
              onClick={handleMatchClick}
              className="inline-flex items-center gap-2 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 transition-colors rounded-full mb-2 cursor-pointer border border-slate-200/50"
            >
              <div className="flex -space-x-1 shrink-0">
                <img src={comment.match_info.homeTeam.logo} alt="" className="w-3.5 h-3.5 object-contain" />
                <img src={comment.match_info.awayTeam.logo} alt="" className="w-3.5 h-3.5 object-contain" />
              </div>
              <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">
                {getTeamName(comment.match_info.homeTeam.id, lang as Language, comment.match_info.homeTeam.name)}
                <span className="mx-1 text-slate-300">vs</span>
                {getTeamName(comment.match_info.awayTeam.id, lang as Language, comment.match_info.awayTeam.name)}
              </span>
              <div className={`w-1.5 h-1.5 rounded-full ${comment.match_info.status === 'Live' ? 'bg-rose-500 animate-pulse' : 'bg-slate-300'}`} />
            </div>
          )}
          
          {/* Comment Content */}
          <div className="mb-3">
            <p className="text-[15px] text-slate-800 leading-[1.4] break-words whitespace-pre-wrap font-medium">
              {showOriginal ? comment.content : (translatedText || comment.content)}
            </p>

            {/* Translation Button */}
            {comment.content && !isSameLanguage && (
              <button
                onClick={handleTranslate}
                disabled={isTranslating}
                className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-indigo-500 hover:text-indigo-600 transition-colors"
              >
                {isTranslating ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Globe size={12} />
                )}
                {showOriginal ? t(lang, 'viewTranslation') : (translatedText ? t(lang, 'viewOriginal') : t(lang, 'viewTranslation'))}
              </button>
            )}

            {comment.image_url && (
              <>
                <div 
                  className="mt-3 rounded-2xl overflow-hidden border border-gray-100 bg-gray-50 aspect-auto max-w-full cursor-zoom-in w-48"
                  onClick={() => setIsZoomOpen(true)}
                >
                  <img 
                    src={comment.image_url} 
                    alt="" 
                    className="w-full h-auto object-cover hover:opacity-95 transition-opacity" 
                  />
                </div>
                <ImageZoomModal 
                  imageUrl={comment.image_url}
                  isOpen={isZoomOpen}
                  onClose={() => setIsZoomOpen(false)}
                />
              </>
            )}
          </div>

          {/* Action Bar (SNS Style) */}
          <div className="flex items-center gap-6 mt-1 text-slate-500">
            <button 
              onClick={() => onLike(comment.id)}
              className={`flex items-center gap-1.5 group transition-colors ${comment.is_liked ? 'text-rose-500' : 'hover:text-rose-500'}`}
            >
              <div className="p-1.5 rounded-full group-hover:bg-rose-50 transition-colors">
                <ThumbsUp size={18} strokeWidth={2} fill={comment.is_liked ? 'currentColor' : 'none'} />
              </div>
              <span className="text-xs font-bold tabular-nums">{comment.likes_count || 0}</span>
            </button>

            <button 
              onClick={() => onReply(comment)}
              className="flex items-center gap-1.5 group hover:text-indigo-500 transition-colors"
            >
              <div className="p-1.5 rounded-full group-hover:bg-indigo-50 transition-colors">
                <MessageSquare size={18} strokeWidth={2} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider">{t(lang, 'reply')}</span>
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};
